import React, { useState } from "react";
import { Box, Button, TextField, Typography, Paper, CircularProgress, Grid, RadioGroup, FormControlLabel, Radio, FormLabel, FormControl, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

function LostReportForm() {
  const [form, setForm] = useState({
    readerName: "",
    readerId: "",
    bookTitle: "",
    bookBarcode: "",
    bookCallNumber: "",
    bookPricing: "",
    compensationMethod: "",
  });
  const [claimFormLoading, setClaimFormLoading] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMsg, setDialogMsg] = useState("");

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCompensationChange = e => {
    const value = e.target.value;
    setForm({ ...form, [e.target.name]: value });
    // 如果選擇 samebook 或 anotherbook，清空定價
    if (value === "samebook" || value === "anotherbook") {
      setForm(prev => ({ ...prev, bookPricing: "" }));
    }
  };

  // 驗證必填欄位
  const validateForm = () => {
    if (!form.readerName || !form.readerId || !form.bookTitle || !form.bookBarcode || !form.bookCallNumber || !form.compensationMethod) {
      setDialogMsg("請填寫所有必填欄位！");
      setDialogOpen(true);
      return false;
    }
    if (form.compensationMethod === "cash" && !form.bookPricing) {
      setDialogMsg("請填寫書籍定價！");
      setDialogOpen(true);
      return false;
    }
    return true;
  };

  const handleClaimFormSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;
    setClaimFormLoading(true);
    try {
      const response = await fetch("/api/lostreport", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lost_report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setDialogMsg("提交表單時發生錯誤，請稍後再試。");
      setDialogOpen(true);
    } finally {
      setClaimFormLoading(false);
    }
  };

  const handleReceiptSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;
    setReceiptLoading(true);
    try {
      const response = await fetch("/api/lostreportreceipt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "receipt.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("提交表單時發生錯誤，請稍後再試。");
    } finally {
      setReceiptLoading(false);
    }
  };

  return (
    <Box
      component={Paper}
      elevation={3}
      sx={{
        width: "85%",
        maxHeight: "80vh",
        mx: "auto",
        mt: 0,
        p: 4,
        borderRadius: 2,
        overflow: "auto",
        position: "relative", // 讓內部 absolute 定位生效
      }}
    >
      {/* 右下角按鈕區 */}
      <Box
        sx={{
          position: "absolute",
          right: 32,
          bottom: 32,
          display: "flex",
          gap: 2,
          zIndex: 10,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          sx={{ minWidth: 120, minHeight: 60, width: 120, height: 60, borderRadius: 2 }}
          onClick={handleClaimFormSubmit}
          disabled={claimFormLoading}
        >
          {claimFormLoading ? <CircularProgress size={24} /> : "產生賠償單"}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          sx={{ minWidth: 120, minHeight: 60, width: 120, height: 60, borderRadius: 2 }}
          onClick={handleReceiptSubmit}
          disabled={receiptLoading || form.compensationMethod !== "cash"}
        >
          {receiptLoading ? <CircularProgress size={24} /> : "收費收據"}
        </Button>
      </Box>
      {/* 原本的表單內容 */}
      <Box component="form" autoComplete="off">
        {/* 讀者資料區塊 */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>讀者資料</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="姓名"
                name="readerName"
                value={form.readerName}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="借閱證號"
                name="readerId"
                value={form.readerId}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
          </Grid>
        </Box>

        {/* 書籍資料區塊 */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>遺失/破損的書籍資料</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="書名"
                name="bookTitle"
                value={form.bookTitle}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="條碼號"
                name="bookBarcode"
                value={form.bookBarcode}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="索書號"
                name="bookCallNumber"
                value={form.bookCallNumber}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="書籍定價(阿拉伯數字)"
                name="bookPricing"
                value={form.bookPricing}
                onChange={handleChange}
                fullWidth
                required={form.compensationMethod === "cash"}
                disabled={form.compensationMethod === "samebook" || form.compensationMethod === "anotherbook"}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 賠償方式區塊 */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>賠償方式</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">請選擇賠償方式</FormLabel>
                <RadioGroup
                  row
                  name="compensationMethod"
                  value={form.compensationMethod}
                  onChange={handleCompensationChange}
                >
                  <FormControlLabel value="cash" control={<Radio />} label="賠一倍現金" />
                  <FormControlLabel value="samebook" control={<Radio />} label="賠同一本書" />
                  <FormControlLabel value="anotherbook" control={<Radio />} label="賠兩本同類型的書" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* 提示對話框 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>無法送出</DialogTitle>
        <DialogContent>
          <Typography>{dialogMsg}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            確認
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LostReportForm;